import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { personelinAtamasi } from '../api/atama';
import { personelKarti } from '../api/organizasyon';
import { HataKutusu } from '../bilesenler/temel';
import { tarihMetni } from '../bicim/tarih';

/**
 * PERSONEL KARTI — DEMO HATTI.
 *
 * TARİH BİÇİMLEME `bicim/tarih.ts`TEN GELİR. Bu dosyada bir zamanlar yerel
 * bir `tarihMetni` vardı ve `new Intl.DateTimeFormat(...).format(new Date(iso))`
 * kullanıyordu: doğum, işe giriş ve çıkış tarihleri SALT TARİH alanları ve
 * ayrıştırıldıklarında negatif ofsetli bir tarayıcıda gün bir geri kayardı.
 * Ortak biçimleyici metni ayrıştırmadan keser.
 *
 * Belge, sağlık, ücret ve iletişim sekmeleri YOK. Ücret ve sağlık ayrı yetki
 * (`UCRET_GORUNTULE`, `SAGLIK_GORUNTULE`) ve KVKK hassas okuma kaydı
 * gerektiriyor; yarım bağlanmış bir sekme, yetkisi olmayan kullanıcıya boş
 * kutu göstermekten öteye gitmez. Kendi bölümünde eklenecek.
 */
export function PersonelKarti() {
  const { personelId } = useParams<{ personelId: string }>();

  const kart = useQuery({
    queryKey: ['personel', 'kart', personelId],
    queryFn: () => personelKarti(personelId ?? ''),
    enabled: Boolean(personelId),
  });

  if (kart.isPending) return <p className="text-sm text-slate-500">Yükleniyor…</p>;
  if (kart.error) return <HataKutusu hata={kart.error} />;
  if (!kart.data) return null;

  const p = kart.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link to="/personel" className="text-sm text-kurum-600 hover:underline">
        ← Personel listesi
      </Link>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">{p.adSoyad}</h1>
            <p className="mt-0.5 font-mono text-sm text-slate-500">{p.personelNo}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/*
              PUANTAJ ÇİZELGESİ BAĞI. Çizelge `atamaId` ile açılıyor ama
              `PersonelOzetiDto` bu alanı taşımıyor — personel listesi
              atamadan bağımsız. Atama ayrı bir uçtan çözülüyor; çıkışı olan
              personelde geçerli atama bulunmadığı için düğme gizlenir.
            */}
            <CizelgeBaglantisi personelId={p.personelId} />

            {p.cikisTarihi && (
              <span className="rounded bg-slate-200 px-2 py-1 text-xs text-slate-700">
                {tarihMetni(p.cikisTarihi)} tarihinde ayrıldı
              </span>
            )}
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Satir etiket="Birim" deger={p.birimAd ?? 'Aktif ataması yok'} />
          <Satir etiket="Pozisyon" deger={p.pozisyonKod ?? '—'} />
          <Satir
            etiket="İstihdam tipi"
            deger={p.istihdamTipiKod}
            not="4857 (işçi) ve 657 (memur) farklı mesai kurallarına tabidir."
          />
          <Satir etiket="Devam yöntemi" deger={DEVAM_ETIKETI[p.devamYontemi] ?? p.devamYontemi} />
          <Satir etiket="İşe giriş" deger={tarihMetni(p.iseGirisTarihi)} />
          <Satir etiket="Doğum tarihi" deger={tarihMetni(p.dogumTarihi)} />
          <Satir
            etiket="TC kimlik no"
            deger={p.tcKimlikMaskeli}
            not="Tam değer ayrı bir yetki ve KVKK erişim kaydı gerektirir."
          />
          <Satir etiket="Engelli kadro" deger={p.engelliKadro ? 'Evet' : 'Hayır'} />
        </dl>

        {!p.kullanicisiVarMi && (
          <p className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Bu personelin sisteme giriş yapabileceği bir hesabı yok. Mesai kaydı basamaz.
          </p>
        )}
      </div>
    </div>
  );
}

const DEVAM_ETIKETI: Record<string, string> = {
  kendi: 'Kendi basar',
  puantor: 'Puantör basar',
  kart: 'Kart (pilotta kullanılmıyor)',
};

function Satir({ etiket, deger, not }: { etiket: string; deger: string; not?: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{deger}</dd>
      {not && <p className="mt-0.5 text-xs text-slate-500">{not}</p>}
    </div>
  );
}

/**
 * Kişinin puantaj çizelgesine bağ.
 *
 * Atama sorgusu AYRI bir istek: personel kartı ucu atama taşımıyor ve
 * taşıması da gerekmiyor — personel ile atama farklı yaşam döngüleri.
 * Sorgu başarısız olursa düğme HİÇ gösterilmez; ölü bağ vermek, bağ
 * vermemekten kötüdür.
 */
function CizelgeBaglantisi({ personelId }: { personelId: string }) {
  const atama = useQuery({
    queryKey: ['atama', 'personel', personelId],
    queryFn: () => personelinAtamasi(personelId),
    retry: false,
  });

  if (atama.data === null || atama.data === undefined) return null;

  return (
    <Link
      to={`/puantaj/${atama.data.atamaId}`}
      className="rounded-md border border-kurum-200 bg-kurum-50 px-3 py-1.5 text-sm font-medium text-kurum-800 hover:bg-kurum-100"
    >
      Puantaj çizelgesi
    </Link>
  );
}
